import React, { useState, useEffect, Fragment, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { questionPoolsAPI, questionActivationAPI, API_URL } from '../services/api';
import AudioPlayer from '../components/AudioPlayer';
import AudioPlaybackSettings, { describeAudioPlayback } from '../components/AudioPlaybackSettings';
import ImageViewer from '../components/ImageViewer';
import FileUpload from '../components/FileUpload';
import { useToast } from '../components/ToastContainer';

interface Question {
  id?: number;
  quiz_id: number;
  text: string;
  question_type: 'multiple_choice' | 'text' | 'audio_response';
  audio_file?: string;
  image_file?: string;
  order_num: number;
  options?: Option[];
  is_active?: boolean;
  correct_text_answer?: string; // For text questions: the correct answer
  requires_correct_answer?: boolean; // Whether this question requires a correct answer to be defined
  allow_multiple_answers?: boolean; // For multiple choice: whether multiple answers can be selected
  insertAfterOrder?: number; // For inserting at a specific position
}

interface Option {
  id?: number;
  question_id: number;
  text: string;
  image_file?: string;
  is_correct: boolean;
}

interface Quiz {
  id: number;
  title: string;
  description: string;
  category?: string;
  quiz_type?: 'quiz' | 'evaluation' | 'assessment' | 'survey';
  time_limit_minutes?: number; // Time limit in minutes
  require_email?: boolean; // Whether email is required for this quiz
  audio_max_plays?: number | null;
  audio_allow_pause?: boolean;
  audio_allow_seek?: boolean;
  questions?: Question[];
}

interface QuestionPool {
  id: number;
  quiz_id: number;
  name: string;
  description?: string;
  is_active: boolean;
  max_questions: number;
}

const EditQuizPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Pools state
  const [pools, setPools] = useState<QuestionPool[]>([]);
  const [activePoolId, setActivePoolId] = useState<number | null>(null);
  const [creatingPool, setCreatingPool] = useState(false);
  const [newPool, setNewPool] = useState<Partial<QuestionPool>>({ name: '', max_questions: 20 });
  
  // Quiz editing state
  const [editingQuiz, setEditingQuiz] = useState(false);
  const [quizForm, setQuizForm] = useState({ 
    title: '', 
    description: '', 
    category: '',
    quiz_type: 'quiz' as 'quiz' | 'evaluation' | 'assessment' | 'survey',
    time_limit_minutes: undefined as number | undefined,
    require_email: true,
    audio_max_plays: null as number | null,
    audio_allow_pause: true,
    audio_allow_seek: true
  });
  
  // Question editing state
  const [editingQuestion, setEditingQuestion] = useState<number | null>(null);
  const [questionForm, setQuestionForm] = useState<Partial<Question>>({});
  const [questionOptions, setQuestionOptions] = useState<Partial<Option>[]>([]);
  
  // New question state
  const [addingQuestion, setAddingQuestion] = useState(false);
  const [insertPosition, setInsertPosition] = useState<number | undefined>(undefined);
  const [newQuestion, setNewQuestion] = useState<Partial<Question>>({
    text: '',
    question_type: 'multiple_choice',
    requires_correct_answer: false,
    correct_text_answer: '',
    allow_multiple_answers: false
  });
  const [newOptions, setNewOptions] = useState<Partial<Option>[]>([
    { text: '', is_correct: true },
    { text: '', is_correct: false }
  ]);
  
  // Media file state
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [questionImageFiles, setQuestionImageFiles] = useState<File[]>([]);
  const [questionImageCaptions, setQuestionImageCaptions] = useState<string[]>([]);
  const [optionImageFiles, setOptionImageFiles] = useState<Record<number, File | null>>({});
  const [newOptionImageFiles, setNewOptionImageFiles] = useState<Record<number, File | null>>({});

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Use the new grouped endpoint
        const response = await api.get(`/api/quizzes/${id}/grouped`);
        const quizData = response.data;
        // Normalize is_active to a defined boolean (default true)
        const normalized = {
          ...quizData,
          questions: (quizData.questions || []).map((q: any) => ({
            ...q,
            is_active: q && q.is_active === false ? false : true
          }))
        };
        setQuiz(normalized);
        setQuizForm({ 
          title: quizData.title, 
          description: quizData.description, 
          category: quizData.category || '',
          quiz_type: quizData.quiz_type || 'quiz',
          time_limit_minutes: quizData.time_limit_minutes,
          require_email: quizData.require_email !== undefined ? quizData.require_email : true,
          audio_max_plays: quizData.audio_max_plays || null,
          audio_allow_pause: quizData.audio_allow_pause !== false,
          audio_allow_seek: quizData.audio_allow_seek !== false
        });
        setLoading(false);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch quiz');
        setLoading(false);
      }
    };
    
    if (id) {
      fetchQuiz();
      // Load pools
      (async () => {
        try {
          const [listRes, activeRes] = await Promise.all([
            questionPoolsAPI.list(id),
            questionPoolsAPI.getActive(id)
          ]);
          setPools(listRes.data || []);
          setActivePoolId(activeRes.data?.id || null);
        } catch (e) {
          // ignore silently for now
        }
      })();
    }
  }, [id]);

  // Warn user about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);


  // Quiz editing functions
  const startEditingQuiz = () => {
    setEditingQuiz(true);
    setHasUnsavedChanges(true);
  };

  const cancelEditingQuiz = () => {
    if (!quiz) return;
    setEditingQuiz(false);
    setQuizForm({ 
      title: quiz.title, 
      description: quiz.description, 
      category: quiz.category || '',
      quiz_type: quiz.quiz_type || 'quiz',
      time_limit_minutes: quiz.time_limit_minutes,
      require_email: quiz.require_email !== undefined ? quiz.require_email : true,
      audio_max_plays: quiz.audio_max_plays || null,
      audio_allow_pause: quiz.audio_allow_pause !== false,
      audio_allow_seek: quiz.audio_allow_seek !== false
    });
    setHasUnsavedChanges(false);
  };

  const saveQuizDetails = async () => {
    if (!id || !quiz) return;
    
    try {
      setSaving(true);
      await api.put(`/api/quizzes/${id}`, quizForm);
      
      setQuiz(prev => prev ? { ...prev, ...quizForm } : null);
      setEditingQuiz(false);
      setHasUnsavedChanges(false);
      showToast('Quiz details saved successfully!', 'success');
    } catch (err: any) {
      console.error('Error saving quiz details:', err);
      showToast('Failed to save quiz details', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Question editing functions
  const startEditingQuestion = (question: Question) => {
    setEditingQuestion(question.id!);
    setQuestionForm(question);
    setQuestionOptions(question.options || []);
    setHasUnsavedChanges(true);
  };

  const cancelEditingQuestion = () => {
    setEditingQuestion(null);
    setQuestionForm({});
    setQuestionOptions([]);
    setAudioFile(null);
    setImageFile(null);
    setHasUnsavedChanges(false);
  };

  const saveQuestion = async () => {
    if (!editingQuestion || !questionForm.text?.trim()) {
      showToast('Please enter a question text', 'error');
      return;
    }

    // Validate options for multiple choice
    if (questionForm.question_type === 'multiple_choice') {
      // Check that each option has either text or an image (including existing images)
      const invalidOptions = questionOptions.some(opt => {
        const hasText = opt.text?.trim();
        const hasImage = opt.image_file || optionImageFiles[opt.id!];
        return !hasText && !hasImage;
      });
      if (invalidOptions) {
        showToast('Each option must have either text or an image', 'error');
        return;
      }
      
      const hasCorrectOption = questionOptions.some(opt => opt.is_correct);
      if (!hasCorrectOption) {
        showToast('Please select at least one correct answer', 'error');
        return;
      }
    }

    // Validate text answer correct answer if required
    if (questionForm.question_type === 'text' && questionForm.requires_correct_answer) {
      if (!questionForm.correct_text_answer?.trim()) {
        showToast('Please provide the correct answer for this text question', 'error');
        return;
      }
    }

    try {
      setSaving(true);

      // Upload media if provided
      let audioFileName = questionForm.audio_file;
      if (audioFile) {
        const uploadedFileName = await uploadAudio();
        if (uploadedFileName) {
          audioFileName = uploadedFileName;
        }
      }
      let imageFileName = questionForm.image_file;
      if (imageFile) {
        const uploadedImage = await uploadImage();
        if (uploadedImage) {
          imageFileName = uploadedImage;
        }
      }

      // Update question
      const updatedQuestionData = {
        ...questionForm,
        audio_file: audioFileName,
        image_file: imageFileName,
        correct_text_answer: questionForm.question_type === 'text' ? questionForm.correct_text_answer : undefined,
        requires_correct_answer: questionForm.question_type === 'text' ? questionForm.requires_correct_answer : false
      };

      await api.put(`/api/quizzes/questions/${editingQuestion}`, updatedQuestionData);

      // Handle options based on question type
      if (questionForm.question_type === 'multiple_choice') {
        // Get current options from the original question
        const originalQuestion = quiz?.questions?.find(q => q.id === editingQuestion);
        const originalOptionIds = originalQuestion?.options?.map(opt => opt.id) || [];
        
        // Delete options that are no longer needed
        for (const optionId of originalOptionIds) {
          const stillExists = questionOptions.some(opt => opt.id === optionId);
          if (!stillExists) {
            await api.delete(`/api/quizzes/options/${optionId}`);
          }
        }
        
        // Update or create options
        for (let idx = 0; idx < questionOptions.length; idx++) {
          const option = questionOptions[idx];
          if (option.id) {
            let image_file = option.image_file;
            const file = optionImageFiles[option.id!];
            if (file) {
              const form = new FormData();
              form.append('image', file);
              const up = await api.post('/api/images/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
              image_file = up.data.imageFile;
            }
            await api.put(`/api/quizzes/options/${option.id}`, {
              text: option.text,
              image_file,
              is_correct: option.is_correct
            });
          } else {
            let image_file: string | undefined = undefined;
            const file = optionImageFiles[idx];
            if (file) {
              const form = new FormData();
              form.append('image', file);
              const up = await api.post('/api/images/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
              image_file = up.data.imageFile;
            }
            await api.post(`/api/quizzes/questions/${editingQuestion}/options`, {
              text: option.text,
              image_file,
              is_correct: option.is_correct
            });
          }
        }
      } else {
        // If question type is not multiple choice, delete all existing options
        const originalQuestion = quiz?.questions?.find(q => q.id === editingQuestion);
        if (originalQuestion?.options) {
          for (const option of originalQuestion.options) {
            await api.delete(`/api/quizzes/options/${option.id}`);
          }
        }
      }

      // Refresh quiz data
      const response = await api.get(`/api/quizzes/${id}`);
      setQuiz(response.data);
      
      setEditingQuestion(null);
      setQuestionForm({});
      setQuestionOptions([]);
      setAudioFile(null);
      setImageFile(null);
      setHasUnsavedChanges(false);
      showToast('Question saved successfully!', 'success');
    } catch (err: any) {
      console.error('Error saving question:', err);
      showToast('Failed to save question', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteQuestion = async (questionId: number) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    try {
      setSaving(true);
      await api.delete(`/api/quizzes/questions/${questionId}`);
      
      // Refresh quiz data
      const response = await api.get(`/api/quizzes/${id}`);
      setQuiz(response.data);
      showToast('Question deleted successfully!', 'success');
    } catch (err: any) {
      console.error('Error deleting question:', err);
      showToast('Failed to delete question', 'error');
    } finally {
      setSaving(false);
    }
  };

  // New question functions
  const startAddingQuestion = (afterOrderNum?: number) => {
    setAddingQuestion(true);
    setInsertPosition(afterOrderNum);
    setHasUnsavedChanges(true);
  };

  const cancelAddingQuestion = () => {
    setAddingQuestion(false);
    setInsertPosition(undefined);
    setNewQuestion({ 
      text: '', 
      question_type: 'multiple_choice',
      requires_correct_answer: false,
      correct_text_answer: ''
    });
    setNewOptions([
      { text: '', is_correct: true },
      { text: '', is_correct: false }
    ]);
    setAudioFile(null);
    setImageFile(null);
    setQuestionImageFiles([]);
    setHasUnsavedChanges(false);
  };

  const addQuestion = async () => {
    if (!id || !quiz || !newQuestion.text?.trim()) {
      showToast('Please enter a question text', 'error');
      return;
    }
    
    // Validate options for multiple choice
    if (newQuestion.question_type === 'multiple_choice') {
      // Check that each option has either text or an image
      const invalidOptions = newOptions.some((opt, index) => {
        const hasText = opt.text?.trim();
        const hasImage = optionImageFiles[index];
        return !hasText && !hasImage;
      });
      if (invalidOptions) {
        showToast('Each option must have either text or an image', 'error');
        return;
      }
      
      const hasCorrectOption = newOptions.some(opt => opt.is_correct);
      if (!hasCorrectOption) {
        showToast('Please select at least one correct answer', 'error');
        return;
      }
    }
    
    // Validate text answer correct answer if required
    if (newQuestion.question_type === 'text' && newQuestion.requires_correct_answer) {
      if (!newQuestion.correct_text_answer?.trim()) {
        showToast('Please provide the correct answer for this text question', 'error');
        return;
      }
    }
    
    try {
      setSaving(true);

      // Upload media if provided
      let audioFileName = null;
      if (audioFile) {
        audioFileName = await uploadAudio();
      }
      let imageFileName = null;
      if (imageFile) {
        imageFileName = await uploadImage();
      }
      
      // Upload gallery images
      const imageFilesUploaded: string[] = [];
      for (const f of questionImageFiles) {
        const form = new FormData();
        form.append('image', f);
        const up = await api.post('/api/images/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        imageFilesUploaded.push(up.data.imageFile);
      }
      
      // Create the question
      const questionData: any = {
        quiz_id: parseInt(id),
        text: newQuestion.text!,
        question_type: newQuestion.question_type!,
        audio_file: audioFileName || undefined,
        image_file: imageFileName || undefined,
        order_num: quiz.questions?.length ? quiz.questions.length + 1 : 1,
        correct_text_answer: newQuestion.question_type === 'text' ? newQuestion.correct_text_answer : undefined,
        requires_correct_answer: newQuestion.question_type === 'text' ? newQuestion.requires_correct_answer : false,
        allow_multiple_answers: newQuestion.question_type === 'multiple_choice' ? newQuestion.allow_multiple_answers : false
      };
      
      // Add insertAfterOrder if we're inserting at a specific position
      if (insertPosition !== undefined) {
        questionData.insertAfterOrder = insertPosition;
      }
      
      const response = await api.post(
        `/api/quizzes/${id}/questions`, 
        imageFilesUploaded.length > 0
          ? { ...questionData, image_files: imageFilesUploaded.map((fn, idx) => ({ image_file: fn, caption: questionImageCaptions[idx] || undefined })) }
          : questionData
      );
      const newQuestionId = response.data.id;
      
      // Add options for multiple choice questions
      if (newQuestion.question_type === 'multiple_choice' && newQuestionId) {
        for (let i = 0; i < newOptions.length; i++) {
          const option = newOptions[i];
          
          // Upload option image if provided
          let optionImageFileName = null;
          const imageFile = newOptionImageFiles[i];
          if (imageFile) {
            const formData = new FormData();
            formData.append('image', imageFile);
            const imageResponse = await api.post('/api/upload/image', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            optionImageFileName = imageResponse.data.filename;
          }
          
          await api.post(`/api/quizzes/questions/${newQuestionId}/options`, {
            text: option.text,
            is_correct: option.is_correct,
            image_file: optionImageFileName || undefined
          });
        }
      }
      
      // Refresh quiz data
      const updatedResponse = await api.get(`/api/quizzes/${id}`);
      setQuiz(updatedResponse.data);
      
      // Reset form
      setAddingQuestion(false);
      setNewQuestion({ 
        text: '', 
        question_type: 'multiple_choice',
        requires_correct_answer: false,
        correct_text_answer: ''
      });
      setNewOptions([
        { text: '', is_correct: true },
        { text: '', is_correct: false }
      ]);
      setAudioFile(null);
      setImageFile(null);
      setQuestionImageFiles([]);
      setQuestionImageCaptions([]);
      setNewOptionImageFiles({});
      setHasUnsavedChanges(false);
      showToast('Question added successfully!', 'success');
    } catch (err: any) {
      console.error('Error adding question:', err);
      showToast('Failed to add question', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Helper functions
  const handleQuestionFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Handle question type change
    if (name === 'question_type') {
      const questionType = value as 'multiple_choice' | 'text' | 'audio_response';
      setQuestionForm(prev => ({ ...prev, [name]: questionType }));
      
      // If changing to multiple choice and no options exist, add default options
      if (questionType === 'multiple_choice' && questionOptions.length === 0) {
        setQuestionOptions([
          { text: '', is_correct: true },
          { text: '', is_correct: false }
        ]);
      }
      // If changing away from multiple choice, we'll keep options but they won't be saved
      // The user can see what they're losing before saving
    } else {
      setQuestionForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleNewQuestionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewQuestion(prev => ({ ...prev, [name]: value }));
  };

  const handleOptionChange = (index: number, field: 'text' | 'is_correct', value: string | boolean, isEditing = false) => {
    if (isEditing) {
      setQuestionOptions(prev => 
        prev.map((option, i) => 
          i === index ? { ...option, [field]: value } : option
        )
      );
    } else {
      setNewOptions(prev => 
        prev.map((option, i) => 
          i === index ? { ...option, [field]: value } : option
        )
      );
    }
  };

  const addOption = (isEditing = false) => {
    if (isEditing) {
      setQuestionOptions(prev => [...prev, { text: '', is_correct: false }]);
    } else {
      setNewOptions(prev => [...prev, { text: '', is_correct: false }]);
    }
  };

  const removeOption = (index: number, isEditing = false) => {
    const options = isEditing ? questionOptions : newOptions;
    if (options.length <= 2) {
      showToast('Multiple choice questions must have at least 2 options', 'error');
      return;
    }
    
    if (isEditing) {
      setQuestionOptions(prev => prev.filter((_, i) => i !== index));
    } else {
      setNewOptions(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleAudioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setAudioFile(e.target.files[0]);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleQuestionImagesAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files!);
      setQuestionImageFiles(prev => [...prev, ...newFiles]);
      setQuestionImageCaptions(prev => [...prev, ...newFiles.map(() => '')]);
    }
  };

  const removeQuestionImageAt = (idx: number) => {
    setQuestionImageFiles(prev => prev.filter((_, i) => i !== idx));
    setQuestionImageCaptions(prev => prev.filter((_, i) => i !== idx));
  };

  const moveQuestionImage = (idx: number, direction: -1 | 1) => {
    setQuestionImageFiles(prev => {
      const arr = [...prev];
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= arr.length) return arr;
      const temp = arr[idx];
      arr[idx] = arr[newIdx];
      arr[newIdx] = temp;
      return arr;
    });
    setQuestionImageCaptions(prev => {
      const arr = [...prev];
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= arr.length) return arr;
      const temp = arr[idx];
      arr[idx] = arr[newIdx];
      arr[newIdx] = temp;
      return arr;
    });
  };

  const uploadAudio = async (): Promise<string | null> => {
    if (!audioFile) return null;
    
    try {
      setUploadingAudio(true);
      
      const formData = new FormData();
      formData.append('audio', audioFile);
      
      const response = await api.post('/api/audio/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadingAudio(false);
      return response.data.audioFile;
    } catch (err) {
      console.error('Error uploading audio:', err);
      setUploadingAudio(false);
      showToast('Failed to upload audio file', 'error');
      return null;
    }
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile) return null;
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      const response = await api.post('/api/images/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.imageFile;
    } catch (err) {
      console.error('Error uploading image:', err);
      showToast('Failed to upload image file', 'error');
      return null;
    }
  };

  const finishEditing = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate('/admin');
      }
    } else {
      navigate('/admin');
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="container">
          <div className="card">
            <p>Loading quiz...</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (error || !quiz) {
    return (
      <div className="page">
        <div className="container">
          <div className="card" style={{ backgroundColor: '#ffebee', color: '#c62828' }}>
            <p>{error || 'Quiz not found'}</p>
            <button 
              className="btn"
              onClick={() => navigate('/admin')}
              style={{ marginTop: '15px' }}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="page">
      <div className="container">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
          <h1 className="page-title">Edit Quiz</h1>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {hasUnsavedChanges && (
              <span style={{ 
                color: '#ff9800', 
                fontSize: '14px', 
                alignSelf: 'center',
                fontWeight: '500'
              }}>
                ⚠️ Unsaved changes
              </span>
            )}
            <button 
              className="btn"
              onClick={finishEditing}
              style={{ backgroundColor: '#4CAF50', color: 'white' }}
            >
              Finish Editing
            </button>
          </div>
        </div>
        
        {/* Quiz Details Section */}
        <div className="card">
          <div className="card-header">
            <h2>Quiz Details</h2>
            {!editingQuiz && (
              <button 
                className="btn"
                onClick={startEditingQuiz}
                style={{ backgroundColor: '#2196F3', color: 'white' }}
              >
                Edit Details
              </button>
            )}
          </div>
          
          {editingQuiz ? (
            <div>
              <div className="form-group">
                <label htmlFor="title">Quiz Title *</label>
                <input
                  id="title"
                  className="form-control"
                  value={quizForm.title}
                  onChange={(e) => setQuizForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter quiz title"
                  maxLength={100}
                />
                <small className="form-text">{quizForm.title.length}/100 characters</small>
              </div>
              
              <div className="form-group">
                <label htmlFor="category">Category</label>
                <input
                  type="text"
                  id="category"
                  className="form-control"
                  value={quizForm.category || ''}
                  onChange={(e) => setQuizForm(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="e.g., MTA, Centerlight, ConEd, etc. (optional)"
                  maxLength={100}
                />
                <small className="form-text">Categorize your quiz for better organization</small>
              </div>
              
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  className="form-control"
                  value={quizForm.description}
                  onChange={(e) => setQuizForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  placeholder="Enter quiz description"
                  maxLength={500}
                />
                <small className="form-text">{quizForm.description.length}/500 characters</small>
              </div>
              
              <div className="form-group">
                <label htmlFor="quiz_type">Quiz Type *</label>
                <select
                  id="quiz_type"
                  className="form-control"
                  value={quizForm.quiz_type || 'quiz'}
                  onChange={(e) => setQuizForm(prev => ({ ...prev, quiz_type: e.target.value as 'quiz' | 'evaluation' | 'assessment' | 'survey' }))}
                >
                  <option value="quiz">Quiz - Regular quiz with scoring</option>
                  <option value="evaluation">Evaluation Form - Trainer evaluation of trainees</option>
                  <option value="assessment">Assessment - Formal assessment</option>
                  <option value="survey">Survey - Feedback collection</option>
                </select>
                <small className="form-text">
                  Choose the type of form. Evaluation forms are for trainers to evaluate trainees and don't have scoring.
                </small>
              </div>
              
              <div className="form-group">
                <label htmlFor="time_limit">Time Limit (Optional)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="number"
                    id="time_limit"
                    className="form-control"
                    value={quizForm.time_limit_minutes || ''}
                    onChange={(e) => setQuizForm(prev => ({ 
                      ...prev, 
                      time_limit_minutes: e.target.value ? parseInt(e.target.value) : undefined 
                    }))}
                    placeholder="Enter time limit"
                    min="1"
                    max="480"
                    style={{ maxWidth: '150px' }}
                  />
                  <span>minutes</span>
                </div>
                <small className="form-text">
                  Set a time limit for this quiz (1-480 minutes). Leave empty for no time limit.
                  {quizForm.time_limit_minutes && (
                    <span style={{ color: '#2196f3', fontWeight: 'bold' }}>
                      {' '}Current: {quizForm.time_limit_minutes} minute{quizForm.time_limit_minutes !== 1 ? 's' : ''}
                    </span>
                  )}
                </small>
              </div>
              
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={quizForm.require_email || false}
                    onChange={(e) => setQuizForm(prev => ({ ...prev, require_email: e.target.checked }))}
                    style={{ marginRight: '8px' }}
                  />
                  Require email address from participants
                </label>
                <small className="form-text">
                  {quizForm.require_email 
                    ? 'Participants must provide an email address to submit this quiz' 
                    : 'Participants can submit this quiz without providing an email address'
                  }
                </small>
              </div>

              <AudioPlaybackSettings
                value={quizForm}
                onChange={(changes) => setQuizForm(prev => ({ ...prev, ...changes }))}
              />
              
              <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                <button 
                  className="btn btn-primary"
                  onClick={saveQuizDetails}
                  disabled={saving || !quizForm.title.trim()}
                >
                  {saving ? 'Saving...' : 'Save Details'}
                </button>
                <button 
                  className="btn"
                  onClick={cancelEditingQuiz}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p><strong>Title:</strong> {quiz.title}</p>
              <p><strong>Category:</strong> {quiz.category || 'No category'}</p>
              <p><strong>Type:</strong> {
                quiz.quiz_type === 'evaluation' ? 'Evaluation Form' :
                quiz.quiz_type === 'assessment' ? 'Assessment' :
                quiz.quiz_type === 'survey' ? 'Survey' :
                'Quiz'
              }</p>
              <p><strong>Description:</strong> {quiz.description || 'No description'}</p>
              <p><strong>Time Limit:</strong> {
                quiz.time_limit_minutes 
                  ? `${quiz.time_limit_minutes} minute${quiz.time_limit_minutes !== 1 ? 's' : ''}` 
                  : 'No time limit'
              }</p>
              <p><strong>Require Email:</strong> {quiz.require_email ? 'Yes' : 'No'}</p>
              <p><strong>Audio Playback:</strong> {describeAudioPlayback(quiz)}</p>
            </div>
          )}
        </div>
        
        {/* Questions Section */}
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center' }}>
            <h2>Questions ({quiz.questions?.length || 0})</h2>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select
                value={activePoolId || ''}
                onChange={async (e) => {
                  if (!id) return;
                  const raw = e.target.value;
                  const poolId = raw ? Number(raw) : null;
                  try {
                    if (poolId) {
                      await questionPoolsAPI.activate(id, poolId);
                      setActivePoolId(poolId);
                      showToast('Activated pool', 'success');
                    } else {
                      // No pool selected → deactivate all pools and activate all questions
                      await api.put(`/api/quizzes/${id}`, { /* no-op to ensure auth */ });
                      // Backend already stores is_active per question; here we simply refresh
                      // and rely on server-side pool logic not to filter when no active pool
                      setActivePoolId(null);
                      showToast('Cleared active pool – all questions active', 'success');
                    }
                    // Reload pools and quiz to keep UI in sync
                    try {
                      const [listRes, activeRes, quizRes] = await Promise.all([
                        questionPoolsAPI.list(id),
                        questionPoolsAPI.getActive(id),
                        api.get(`/api/quizzes/${id}/grouped`)
                      ]);
                      setPools(listRes.data || []);
                      setActivePoolId(activeRes.data?.id || null);
                      setQuiz(quizRes.data);
                    } catch {}
                  } catch {
                    showToast('Failed to update pool selection', 'error');
                  }
                }}
                className="form-control"
                style={{ maxWidth: '240px' }}
              >
                <option value="">Select active pool...</option>
                {pools.map(p => (
                  <option key={p.id} value={p.id}>{p.name} ({p.max_questions})</option>
                ))}
              </select>
              <button
                className="btn"
                disabled={!activePoolId}
                onClick={async () => {
                  if (!id || !activePoolId) return;
                  try {
                    await questionPoolsAPI.randomize(id, activePoolId);
                    showToast('Randomized active questions', 'success');
                    // Reload quiz to reflect updated active flags in DB
                    try {
                      const response = await api.get(`/api/quizzes/${id}/grouped`);
                      const quizData = response.data;
                      setQuiz(quizData);
                    } catch {}
                  } catch {
                    showToast('Failed to randomize', 'error');
                  }
                }}
              >Randomize</button>
              <button className="btn btn-primary" onClick={() => setCreatingPool(true)}>New Pool</button>
              {activePoolId && (
                <button
                  className="btn"
                  onClick={async () => {
                    if (!id || !activePoolId) return;
                    const confirmDelete = window.confirm('Delete this pool? This does not delete questions.');
                    if (!confirmDelete) return;
                    try {
                      await questionPoolsAPI.remove(id, activePoolId);
                      // refresh pools
                      const listRes = await questionPoolsAPI.list(id);
                      setPools(listRes.data || []);
                      setActivePoolId(null);
                      showToast('Deleted pool', 'success');
                    } catch {
                      showToast('Failed to delete pool', 'error');
                    }
                  }}
                >Delete Pool</button>
              )}
              {!addingQuestion && (
                <button 
                  className="btn btn-primary"
                  onClick={() => startAddingQuestion()}
                >
                  Add Question
                </button>
              )}
            </div>
          </div>
          
          {/* Create Pool Modal */}
          {creatingPool && (
            <div className="modal-backdrop">
              <div className="modal">
                <h3>Create Question Pool</h3>
                <div className="form-group">
                  <label>Name</label>
                  <input
                    className="form-control"
                    value={newPool.name || ''}
                    onChange={(e) => setNewPool(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Final Exam"
                  />
                </div>
                <div className="form-group">
                  <label>Max Questions</label>
                  <input
                    className="form-control"
                    type="number"
                    min={1}
                    value={newPool.max_questions || 20}
                    onChange={(e) => setNewPool(prev => ({ ...prev, max_questions: Number(e.target.value) }))}
                  />
                </div>
                <div className="form-group">
                  <label>Description (optional)</label>
                  <input
                    className="form-control"
                    value={newPool.description || ''}
                    onChange={(e) => setNewPool(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Short description"
                  />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="btn" onClick={() => setCreatingPool(false)}>Cancel</button>
                  <button
                    className="btn btn-primary"
                    onClick={async () => {
                      if (!id || !newPool.name || !newPool.max_questions) return;
                      try {
                        const resp = await questionPoolsAPI.create(id, {
                          name: newPool.name,
                          max_questions: newPool.max_questions,
                          description: newPool.description,
                        });
                        setPools(prev => [resp.data, ...prev]);
                        setCreatingPool(false);
                        setNewPool({ name: '', max_questions: 20 });
                        showToast('Created pool', 'success');
                      } catch {
                        showToast('Failed to create pool', 'error');
                      }
                    }}
                  >Create</button>
                </div>
              </div>
            </div>
          )}

          {quiz.questions && quiz.questions.length > 0 ? (
            <div className="question-list">
              {quiz.questions.map((question, index) => (
                <React.Fragment key={question.id}>
                  {/* Insert button before each question (except the first) */}
                  {index === 0 && !addingQuestion && (
                    <button
                      className="insert-question-btn"
                      onClick={() => startAddingQuestion(0)}
                    >
                      Insert Question Here
                    </button>
                  )}
                  
                  <div className="question-item">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      {/* <span style={{ opacity: 0.75 }}>
                        Question #{index + 1}
                      </span> */}
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                          type="checkbox"
                          checked={!!question.is_active}
                          onChange={async (e) => {
                            const desired = e.target.checked;
                            try {
                              await questionActivationAPI.set(question.id!, desired);
                              // re-fetch single quiz to ensure consistency
                              const response = await api.get(`/api/quizzes/${id}/grouped`);
                              const quizData = response.data;
                              const normalized = {
                                ...quizData,
                                questions: (quizData.questions || []).map((q: any) => ({
                                  ...q,
                                  is_active: q && q.is_active === false ? false : true
                                }))
                              };
                              setQuiz(normalized);
                              showToast(desired ? 'Question activated' : 'Question deactivated', 'success');
                            } catch {
                              showToast('Failed to update active status', 'error');
                            }
                          }}
                        />
                        Active
                      </label>
                    </div>
                  {editingQuestion === question.id ? (
                    // Edit mode
                    <div className="question-edit">
                      <div className="form-group">
                        <label>Question Text *</label>
                        <textarea
                          className="form-control"
                          name="text"
                          value={questionForm.text || ''}
                          onChange={handleQuestionFormChange}
                          rows={3}
                          placeholder="Enter your question"
                          maxLength={500}
                        />
                        <small className="form-text">{(questionForm.text || '').length}/500 characters</small>
                      </div>
                      
                      <div className="form-group">
                        <label>Question Type</label>
                        <select
                          className="form-control"
                          name="question_type"
                          value={questionForm.question_type || 'multiple_choice'}
                          onChange={handleQuestionFormChange}
                        >
                          <option value="multiple_choice">Multiple Choice</option>
                          <option value="text">Text Answer</option>
                          <option value="audio_response">Audio Response</option>
                        </select>
                      </div>
                      
                      <FileUpload
                        label="Audio File (Optional)"
                        accept="audio/*"
                        file={audioFile}
                        existingFile={questionForm.audio_file}
                        onFileChange={setAudioFile}
                        placeholder="Upload an audio file that goes with this question"
                      />

                      <FileUpload
                        label="Image (Optional)"
                        accept="image/*"
                        file={imageFile}
                        existingFile={questionForm.image_file}
                        onFileChange={setImageFile}
                        placeholder="Upload an image that goes with this question"
                      />

                      {/* Question Images Gallery (multiple) */}
                      <QuestionImagesEditor questionId={editingQuestion!} questionData={questionForm} />
                      
                      {/* Text Answer Settings */}
                      {questionForm.question_type === 'text' && (
                        <div className="text-answer-settings">
                          <h4>Text Answer Settings</h4>
                          
                          <div className="form-group">
                            <label>
                              <input
                                type="checkbox"
                                checked={questionForm.requires_correct_answer || false}
                                onChange={(e) => setQuestionForm(prev => ({
                                  ...prev,
                                  requires_correct_answer: e.target.checked,
                                  correct_text_answer: e.target.checked ? prev.correct_text_answer : ''
                                }))}
                                style={{ marginRight: '8px' }}
                              />
                              This question has a specific correct answer
                            </label>
                            <small className="form-text">
                              Check this if you want to define what the correct answer should be for grading purposes.
                            </small>
                          </div>
                          
                          {questionForm.requires_correct_answer && (
                            <div className="form-group">
                              <label htmlFor="edit_correct_text_answer">Correct Answer *</label>
                              <textarea
                                id="edit_correct_text_answer"
                                className="form-control"
                                value={questionForm.correct_text_answer || ''}
                                onChange={(e) => setQuestionForm(prev => ({
                                  ...prev,
                                  correct_text_answer: e.target.value
                                }))}
                                rows={2}
                                placeholder="Enter the correct answer for this question"
                                maxLength={500}
                              />
                              <small className="form-text">
                                This answer will be used to automatically grade text responses. 
                                Comparison is case-insensitive and ignores punctuation.
                                {questionForm.correct_text_answer && (
                                  <span style={{ color: '#2196f3', fontWeight: 'bold' }}>
                                    {' '}Length: {questionForm.correct_text_answer.length}/500
                                  </span>
                                )}
                              </small>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {questionForm.question_type === 'multiple_choice' && (
                        <div className="options-section">
                          <h4>Options</h4>
                          
                          {/* Toggle for multiple answers */}
                          <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <input
                                type="checkbox"
                                checked={questionForm.allow_multiple_answers || false}
                                onChange={(e) => {
                                  setQuestionForm(prev => ({
                                    ...prev,
                                    allow_multiple_answers: e.target.checked
                                  }));
                                  // Reset options to have at least one correct answer
                                  if (e.target.checked && !questionOptions.some(opt => opt.is_correct)) {
                                    setQuestionOptions(prev => prev.map((opt, i) => ({
                                      ...opt,
                                      is_correct: i === 0
                                    })));
                                  }
                                }}
                              />
                              <span>Allow multiple correct answers</span>
                            </label>
                            <small className="form-text">
                              {questionForm.allow_multiple_answers 
                                ? '✅ Participants can select multiple answers. Partial credit will be given based on correct selections.'
                                : '⭕ Participants can only select one answer.'}
                            </small>
                          </div>
                          
                          {questionOptions.map((option, index) => (
                            <div className="option-form" key={index}>
                              <div className="form-group">
                                <label>
                                  Option {index + 1}
                                  <input
                                    type={questionForm.allow_multiple_answers ? "checkbox" : "radio"}
                                    name="correct-option-edit"
                                    checked={option.is_correct}
                                    onChange={() => {
                                      if (questionForm.allow_multiple_answers) {
                                        // For multiple answers, toggle the checkbox
                                        handleOptionChange(index, 'is_correct', !option.is_correct, true);
                                      } else {
                                        // For single answer, use radio button behavior
                                        setQuestionOptions(prev => 
                                          prev.map((opt, i) => ({
                                            ...opt,
                                            is_correct: i === index
                                          }))
                                        );
                                      }
                                    }}
                                    style={{ marginLeft: '10px' }}
                                  />
                                  <span style={{ marginLeft: '5px' }}>Correct</span>
                                </label>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                  <input
                                    className="form-control"
                                    value={option.text || ''}
                                    onChange={(e) => handleOptionChange(index, 'text', e.target.value, true)}
                                    placeholder={`Option ${index + 1}`}
                                  />
                                  <input type="file" accept="image/*" onChange={(e)=> setOptionImageFiles(prev=>({ ...prev, [option.id || index]: e.target.files && e.target.files[0] ? e.target.files[0] : null }))} className="form-control" />
                                  <button
                                    type="button"
                                    onClick={() => removeOption(index, true)}
                                    className="btn"
                                    style={{ backgroundColor: '#f44336', color: 'white' }}
                                  >
                                    Delete
                                  </button>
                                </div>
                                {/* Display existing option image */}
                                {option.image_file && (
                                  <div style={{ marginTop: '10px' }}>
                                    <ImageViewer src={`${API_URL}/uploads/${option.image_file}`} title={`Option ${index + 1} Image`} />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          
                          {questionForm.allow_multiple_answers && (
                            <div className="alert alert-info" style={{ marginTop: '10px', marginBottom: '10px' }}>
                              <strong>ℹ️ Multiple Answers Mode:</strong> Select all correct options. Participants will receive partial credit based on their selections.
                            </div>
                          )}
                          
                          <button
                            type="button"
                            onClick={() => addOption(true)}
                            className="btn"
                            style={{ marginTop: '10px' }}
                          >
                            Add Option
                          </button>
                        </div>
                      )}
                      
                      <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                        <button
                          className="btn btn-primary"
                          onClick={saveQuestion}
                          disabled={saving || uploadingAudio}
                        >
                          {saving ? 'Saving...' : uploadingAudio ? 'Uploading...' : 'Save Question'}
                        </button>
                        <button
                          className="btn"
                          onClick={cancelEditingQuestion}
                          disabled={saving || uploadingAudio}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View mode
                    <div>
                      <div className="question-header">
                        <div>
                          <span className="question-number">Q{question.order_num}</span>
                          <span className="question-type-badge">{question.question_type}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button
                            className="btn"
                            onClick={() => startEditingQuestion(question)}
                            style={{ backgroundColor: '#2196F3', color: 'white', fontSize: '12px', padding: '5px 10px' }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn"
                            onClick={() => deleteQuestion(question.id!)}
                            style={{ backgroundColor: '#f44336', color: 'white', fontSize: '12px', padding: '5px 10px' }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      
                      <div className="question-text">
                        <strong>{question.text}</strong>
                      </div>
                      
                      {question.audio_file && (
                        <AudioPlayer 
                          src={`${API_URL}/uploads/${question.audio_file}`} 
                          title="Question Audio" 
                        />
                      )}

                      {question.image_file && (
                        <div style={{ marginTop: '10px' }}>
                          <ImageViewer src={`${API_URL}/uploads/${question.image_file}`} title="Question Image" />
                        </div>
                      )}
                      
                      {question.question_type === 'multiple_choice' && question.options && (
                        <>
                          {question.allow_multiple_answers && (
                            <div className="badge badge-info" style={{ marginBottom: '8px' }}>
                              Multiple Answers ({question.options.filter(o => o.is_correct).length} correct)
                            </div>
                          )}
                          <div className="option-list">
                            {question.options.map(option => (
                              <div 
                                className={`option-item ${option.is_correct ? 'correct-option' : ''}`} 
                                key={option.id}
                              >
                                {option.is_correct ? '✅' : '○'} {option.text}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                      
                      {question.question_type === 'text' && (
                        <div className="text-answer-info">
                          {question.requires_correct_answer ? (
                            <div className="alert alert-info" style={{padding: '8px', margin: '8px 0', fontSize: '0.9em'}}>
                              Has correct answer: <em>"{question.correct_text_answer}"</em>
                            </div>
                          ) : (
                            <div className="alert alert-secondary" style={{padding: '8px', margin: '8px 0', fontSize: '0.9em'}}>
                              Open-ended (no specific correct answer)
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Insert button after each question */}
                {!addingQuestion && (
                  <button
                    className="insert-question-btn"
                    onClick={() => startAddingQuestion(question.order_num)}
                  >
                    Insert Question Here
                  </button>
                )}
                </React.Fragment>
              ))}
            </div>
          ) : (
            <p style={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', margin: '20px 0' }}>
              No questions yet. Add your first question below! 👇
            </p>
          )}
        </div>
        
        {/* Add New Question Section */}
        {addingQuestion && (
          <div className="card">
            <div className="card-header">
              <h2>➕ Add New Question {insertPosition !== undefined ? `(After Q${insertPosition})` : ''}</h2>
            </div>
            
            <div className="form-group">
              <label htmlFor="new-text">Question Text *</label>
              <textarea
                id="new-text"
                name="text"
                className="form-control"
                value={newQuestion.text}
                onChange={handleNewQuestionChange}
                rows={3}
                placeholder="Enter your question"
                maxLength={500}
              />
              <small className="form-text">{(newQuestion.text || '').length}/500 characters</small>
            </div>
            
            <div className="form-group">
              <label htmlFor="new-question_type">Question Type</label>
              <select
                id="new-question_type"
                name="question_type"
                className="form-control"
                value={newQuestion.question_type}
                onChange={handleNewQuestionChange}
              >
                <option value="multiple_choice">Multiple Choice</option>
                <option value="text">Text Answer</option>
                <option value="audio_response">Audio Response</option>
              </select>
            </div>
            
            <FileUpload
              label="Audio File (Optional)"
              accept="audio/*"
              file={audioFile}
              onFileChange={setAudioFile}
              placeholder="Upload an audio file that goes with this question"
            />

            <FileUpload
              label="Image (Optional)"
              accept="image/*"
              file={imageFile}
              onFileChange={setImageFile}
              placeholder="Upload an image that goes with this question"
            />

            {/* Question Images Gallery */}
            <div className="form-group">
              <label>Question Images (Gallery, Optional)</label>
              <input type="file" accept="image/*" multiple onChange={handleQuestionImagesAdd} className="form-control" />
              {questionImageFiles.length > 0 && (
                <div className="gallery" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  {questionImageFiles.map((file, idx) => (
                    <div key={idx} style={{ border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: 8 }}>
                      <img src={URL.createObjectURL(file)} alt={`qimg-${idx}`} style={{ maxHeight: 100, display: 'block', borderRadius: 6 }} />
                      <input
                        type="text"
                        placeholder="Enter caption (optional)"
                        value={questionImageCaptions[idx] || ''}
                        onChange={(e) => {
                          const newCaptions = [...questionImageCaptions];
                          newCaptions[idx] = e.target.value;
                          setQuestionImageCaptions(newCaptions);
                        }}
                        style={{
                          width: '100%',
                          marginTop: 4,
                          padding: '4px 6px',
                          fontSize: '0.8em',
                          border: '1px solid #ccc',
                          borderRadius: 4
                        }}
                      />
                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                        <button type="button" className="btn btn-small" onClick={() => moveQuestionImage(idx, -1)} disabled={idx===0}>↑</button>
                        <button type="button" className="btn btn-small" onClick={() => moveQuestionImage(idx, 1)} disabled={idx===questionImageFiles.length-1}>↓</button>
                        <button type="button" className="btn btn-danger btn-small" onClick={() => removeQuestionImageAt(idx)}>Remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <small className="form-text">Add, remove, and reorder multiple images for this question.</small>
            </div>
            
            {/* Text Answer Settings for New Question */}
            {newQuestion.question_type === 'text' && (
              <div className="text-answer-settings">
                <h4>Text Answer Settings</h4>
                
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={newQuestion.requires_correct_answer || false}
                      onChange={(e) => setNewQuestion(prev => ({
                        ...prev,
                        requires_correct_answer: e.target.checked,
                        correct_text_answer: e.target.checked ? prev.correct_text_answer : ''
                      }))}
                      style={{ marginRight: '8px' }}
                    />
                    This question has a specific correct answer
                  </label>
                  <small className="form-text">
                    Check this if you want to define what the correct answer should be for grading purposes.
                  </small>
                </div>
                
                {newQuestion.requires_correct_answer && (
                  <div className="form-group">
                    <label htmlFor="new_correct_text_answer">Correct Answer *</label>
                    <textarea
                      id="new_correct_text_answer"
                      className="form-control"
                      value={newQuestion.correct_text_answer || ''}
                      onChange={(e) => setNewQuestion(prev => ({
                        ...prev,
                        correct_text_answer: e.target.value
                      }))}
                      rows={2}
                      placeholder="Enter the correct answer for this question"
                      maxLength={500}
                    />
                    <small className="form-text">
                      This answer will be used to automatically grade text responses. 
                      Comparison is case-insensitive and ignores punctuation.
                      {newQuestion.correct_text_answer && (
                        <span style={{ color: '#2196f3', fontWeight: 'bold' }}>
                          {' '}Length: {newQuestion.correct_text_answer.length}/500
                        </span>
                      )}
                    </small>
                  </div>
                )}
              </div>
            )}
            
            {newQuestion.question_type === 'multiple_choice' && (
              <div className="options-section">
                <h3>Options</h3>
                
                {/* Toggle for multiple answers */}
                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="checkbox"
                      checked={newQuestion.allow_multiple_answers || false}
                      onChange={(e) => {
                        setNewQuestion(prev => ({
                          ...prev,
                          allow_multiple_answers: e.target.checked
                        }));
                        // Reset options to have at least one correct answer
                        if (e.target.checked && !newOptions.some(opt => opt.is_correct)) {
                          setNewOptions(prev => prev.map((opt, i) => ({
                            ...opt,
                            is_correct: i === 0
                          })));
                        }
                      }}
                    />
                    <span>Allow multiple correct answers</span>
                  </label>
                  <small className="form-text">
                    {newQuestion.allow_multiple_answers 
                      ? '✅ Participants can select multiple answers. Partial credit will be given based on correct selections.'
                      : '⭕ Participants can only select one answer.'}
                  </small>
                </div>
                
                {newOptions.map((option, index) => (
                  <div className="option-form" key={index}>
                    <div className="form-group">
                      <label htmlFor={`new-option-${index}`}>
                        Option {index + 1} 
                        <input
                          type={newQuestion.allow_multiple_answers ? "checkbox" : "radio"}
                          name="correct-option-new"
                          checked={option.is_correct}
                          onChange={() => {
                            if (newQuestion.allow_multiple_answers) {
                              // For multiple answers, toggle the checkbox
                              handleOptionChange(index, 'is_correct', !option.is_correct, false);
                            } else {
                              // For single answer, use radio button behavior
                              setNewOptions(prev => 
                                prev.map((opt, i) => ({
                                  ...opt,
                                  is_correct: i === index
                                }))
                              );
                            }
                          }}
                          style={{ marginLeft: '10px' }}
                        />
                        <span style={{ marginLeft: '5px' }}>Correct</span>
                      </label>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <input
                          id={`new-option-${index}`}
                          className="form-control"
                          value={option.text}
                          onChange={(e) => handleOptionChange(index, 'text', e.target.value, false)}
                          placeholder={`Option ${index + 1}`}
                        />
                        <input 
                          type="file" 
                          accept="image/*" 
                          onChange={(e) => setNewOptionImageFiles(prev => ({ 
                            ...prev, 
                            [index]: e.target.files && e.target.files[0] ? e.target.files[0] : null 
                          }))} 
                          className="form-control" 
                        />
                        <button
                          type="button"
                          onClick={() => removeOption(index, false)}
                          className="btn"
                          style={{ backgroundColor: '#f44336', color: 'white' }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {newQuestion.allow_multiple_answers && (
                  <div className="alert alert-info" style={{ marginTop: '10px', marginBottom: '10px' }}>
                    <strong>ℹ️ Multiple Answers Mode:</strong> Select all correct options. Participants will receive partial credit based on their selections.
                  </div>
                )}
                
                <button
                  type="button"
                  onClick={() => addOption(false)}
                  className="btn"
                  style={{ marginTop: '10px' }}
                >
                  Add Option
                </button>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                className="btn btn-primary"
                onClick={addQuestion}
                disabled={saving || uploadingAudio}
              >
                {saving ? 'Adding...' : uploadingAudio ? 'Uploading...' : 'Add Question'}
              </button>
              <button
                className="btn"
                onClick={cancelAddingQuestion}
                disabled={saving || uploadingAudio}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          flex-wrap: wrap;
          gap: 10px;
        }
        
        .question-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          flex-wrap: wrap;
          gap: 10px;
        }
        
        .form-text {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.6);
          margin-top: 0.25rem;
        }
        
        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        @media (max-width: 768px) {
          .card-header, .question-header {
            flex-direction: column;
            align-items: stretch;
          }
          
          .card-header > div, .question-header > div {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default EditQuizPage; 

// Local inline component to manage multiple images per question
const QuestionImagesEditor: React.FC<{ questionId: number; questionData: any }> = ({ questionId, questionData }) => {
  const [files, setFiles] = React.useState<File[]>([]);
  const [newImageCaptions, setNewImageCaptions] = React.useState<string[]>([]);
  const [initialLoaded, setInitialLoaded] = React.useState<{ image_file: string }[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [existingImages, setExistingImages] = React.useState<{ image_file: string; caption?: string; order_num?: number }[]>([]);
  const [editingCaptions, setEditingCaptions] = React.useState<{[key: number]: string}>({});

  // Load existing images when component mounts
  React.useEffect(() => {
    const loadExistingImages = async () => {
      try {
        // Since there's no direct endpoint, we'll get the quiz data and find this question's images
        const response = await api.get(`/api/quizzes/${questionData.quiz_id}/grouped`);
        const question = response.data.questions?.find((q: any) => q.id === questionId);
        if (question && question.images) {
          setExistingImages(question.images);
        }
      } catch (error) {
        console.error('Error loading existing images:', error);
      }
    };
    
    if (questionId && questionData?.quiz_id) {
      loadExistingImages();
    }
  }, [questionId, questionData?.quiz_id]);

  const addFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files!);
      setFiles(prev => [...prev, ...newFiles]);
      setNewImageCaptions(prev => [...prev, ...newFiles.map(() => '')]);
    }
  };

  const removeAt = (idx: number) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
    setNewImageCaptions(prev => prev.filter((_, i) => i !== idx));
  };
  const move = (idx: number, dir: -1 | 1) => {
    setFiles(prev => {
      const arr = [...prev];
      const ni = idx + dir;
      if (ni < 0 || ni >= arr.length) return arr;
      const t = arr[idx]; arr[idx] = arr[ni]; arr[ni] = t; return arr;
    });
    setNewImageCaptions(prev => {
      const arr = [...prev];
      const ni = idx + dir;
      if (ni < 0 || ni >= arr.length) return arr;
      const t = arr[idx]; arr[idx] = arr[ni]; arr[ni] = t; return arr;
    });
  };

  const removeExistingImage = async (index: number) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      const updatedImages = existingImages.filter((_, i) => i !== index);
      const updateData = {
        ...questionData,
        image_files: updatedImages.map(img => ({ image_file: img.image_file, caption: img.caption }))
      };
      
      await api.put(`/api/quizzes/questions/${questionId}`, updateData);
      setExistingImages(updatedImages);
      alert('Image deleted successfully!');
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image');
    }
  };

  const moveExistingImage = async (index: number, direction: number) => {
    if (index + direction < 0 || index + direction >= existingImages.length) return;
    
    try {
      const updatedImages = [...existingImages];
      [updatedImages[index], updatedImages[index + direction]] = [updatedImages[index + direction], updatedImages[index]];
      
      const updateData = {
        ...questionData,
        image_files: updatedImages.map(img => ({ image_file: img.image_file, caption: img.caption }))
      };
      
      await api.put(`/api/quizzes/questions/${questionId}`, updateData);
      setExistingImages(updatedImages);
    } catch (error) {
      console.error('Error reordering images:', error);
      alert('Failed to reorder images');
    }
  };

  const startEditingCaption = (index: number) => {
    setEditingCaptions(prev => ({
      ...prev,
      [index]: existingImages[index].caption || ''
    }));
  };

  const saveCaption = async (index: number) => {
    try {
      const updatedImages = [...existingImages];
      updatedImages[index] = {
        ...updatedImages[index],
        caption: editingCaptions[index] || undefined
      };
      
      const updateData = {
        ...questionData,
        image_files: updatedImages.map(img => ({ image_file: img.image_file, caption: img.caption }))
      };
      
      await api.put(`/api/quizzes/questions/${questionId}`, updateData);
      setExistingImages(updatedImages);
      setEditingCaptions(prev => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });
    } catch (error) {
      console.error('Error updating caption:', error);
      alert('Failed to update caption');
    }
  };

  const cancelEditingCaption = (index: number) => {
    setEditingCaptions(prev => {
      const newState = { ...prev };
      delete newState[index];
      return newState;
    });
  };

  const save = async () => {
    if (!questionData) {
      alert('Question data not available. Please try again.');
      return;
    }

    try {
      setLoading(true);
      const uploaded: string[] = [];
      for (const f of files) {
        const form = new FormData();
        form.append('image', f);
        const up = await api.post('/api/images/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        uploaded.push(up.data.imageFile);
      }
      
      // Include all current question data plus the new image files
      const updateData = {
        ...questionData,
        image_files: uploaded.map((fn, idx) => ({ image_file: fn, caption: newImageCaptions[idx] || undefined }))
      };
      
      console.log('Sending update data:', updateData);
      console.log('Image files being sent:', uploaded);
      
      await api.put(`/api/quizzes/questions/${questionId}`, updateData);
      alert('Question images saved successfully!');
      setFiles([]); // Clear the files after successful save
      setNewImageCaptions([]); // Clear the captions after successful save
      
      // Reload existing images to show the newly saved ones
      try {
        const response = await api.get(`/api/quizzes/${questionData.quiz_id}/grouped`);
        const question = response.data.questions?.find((q: any) => q.id === questionId);
        if (question && question.images) {
          setExistingImages(question.images);
        }
      } catch (error) {
        console.error('Error reloading images:', error);
      }
    } catch (e: any) {
      console.error('Error saving question images:', e);
      const errorMessage = e.response?.data?.message || e.message || 'Failed to save question images';
      alert(`Failed to save question images: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-group">
      <label>Question Images (Gallery)</label>
      <input 
        type="file" 
        accept="image/*" 
        multiple 
        onChange={addFiles} 
        className="form-control"
        value="" // Reset input value to prevent showing "No file chosen" when images exist
      />
      
      {/* Show existing images */}
      {existingImages.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <label style={{ fontSize: '0.9em', color: 'rgba(255,255,255,0.7)' }}>Existing Images:</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            {existingImages.map((img, idx) => (
              <div key={idx} style={{ border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: 8, position: 'relative' }}>
                <img src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/uploads/${img.image_file}`} alt={`existing-${idx}`} style={{ maxHeight: 100, display: 'block', borderRadius: 6 }} />
                
                {/* Caption display/edit */}
                <div style={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>
                  {editingCaptions[idx] !== undefined ? (
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <input
                        type="text"
                        value={editingCaptions[idx]}
                        onChange={(e) => setEditingCaptions(prev => ({ ...prev, [idx]: e.target.value }))}
                        style={{ 
                          fontSize: '0.8em', 
                          padding: '2px 4px', 
                          border: '1px solid #ccc', 
                          borderRadius: 3,
                          width: '100%'
                        }}
                        placeholder="Enter caption..."
                      />
                      <button
                        type="button"
                        onClick={() => saveCaption(idx)}
                        style={{ 
                          fontSize: '0.7em', 
                          padding: '2px 4px', 
                          background: '#28a745', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: 3,
                          cursor: 'pointer'
                        }}
                      >
                        ✓
                      </button>
                      <button
                        type="button"
                        onClick={() => cancelEditingCaption(idx)}
                        style={{ 
                          fontSize: '0.7em', 
                          padding: '2px 4px', 
                          background: '#dc3545', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: 3,
                          cursor: 'pointer'
                        }}
                      >
                        ✗
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{img.caption || 'No caption'}</span>
                      <button
                        type="button"
                        onClick={() => startEditingCaption(idx)}
                        style={{ 
                          fontSize: '0.7em', 
                          padding: '1px 3px', 
                          background: '#007bff', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: 2,
                          cursor: 'pointer'
                        }}
                      >
                        Edit
                      </button>
                    </div>
                  )}
                </div>

                {/* Control buttons */}
                <div style={{ display: 'flex', gap: 4, marginTop: 6, justifyContent: 'center' }}>
                  <button
                    type="button"
                    onClick={() => moveExistingImage(idx, -1)}
                    disabled={idx === 0}
                    style={{
                      fontSize: '0.7em',
                      padding: '2px 4px',
                      background: idx === 0 ? '#6c757d' : '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: 3,
                      cursor: idx === 0 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => moveExistingImage(idx, 1)}
                    disabled={idx === existingImages.length - 1}
                    style={{
                      fontSize: '0.7em',
                      padding: '2px 4px',
                      background: idx === existingImages.length - 1 ? '#6c757d' : '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: 3,
                      cursor: idx === existingImages.length - 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => removeExistingImage(idx)}
                    style={{
                      fontSize: '0.7em',
                      padding: '2px 4px',
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: 3,
                      cursor: 'pointer'
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Show new files to be uploaded */}
      {(files.length > 0) && (
        <div style={{ marginTop: 8 }}>
          <label style={{ fontSize: '0.9em', color: 'rgba(255,255,255,0.7)' }}>New Images to Upload:</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            {files.map((f, idx) => (
              <div key={idx} style={{ border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: 8 }}>
                <img src={URL.createObjectURL(f)} alt={`new-${idx}`} style={{ maxHeight: 100, display: 'block', borderRadius: 6 }} />
                <input
                  type="text"
                  placeholder="Enter caption (optional)"
                  value={newImageCaptions[idx] || ''}
                  onChange={(e) => {
                    const newCaptions = [...newImageCaptions];
                    newCaptions[idx] = e.target.value;
                    setNewImageCaptions(newCaptions);
                  }}
                  style={{
                    width: '100%',
                    marginTop: 4,
                    padding: '4px 6px',
                    fontSize: '0.8em',
                    border: '1px solid #ccc',
                    borderRadius: 4
                  }}
                />
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button type="button" className="btn btn-small" onClick={() => move(idx, -1)} disabled={idx===0}>↑</button>
                  <button type="button" className="btn btn-small" onClick={() => move(idx, 1)} disabled={idx===files.length-1}>↓</button>
                  <button type="button" className="btn btn-danger btn-small" onClick={() => removeAt(idx)}>Remove</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="d-flex gap-2" style={{ marginTop: 8 }}>
        <button type="button" className="btn btn-primary" onClick={save} disabled={loading || files.length===0}>{loading ? 'Saving...' : 'Save Images'}</button>
      </div>
    </div>
  );
};